import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Analytics } from '@/types'

interface EquipmentHealthOverviewProps {
  healthData?: Analytics['metrics']['equipmentHealth']
  isLoading: boolean
  onViewEquipment: (status: string) => void
}

export default function EquipmentHealthOverview({ 
  healthData, 
  isLoading, 
  onViewEquipment 
}: EquipmentHealthOverviewProps) {
  const healthCategories = [
    { 
      key: 'excellent', 
      label: 'Excellent', 
      color: 'text-success-green', 
      bg: 'bg-emerald-50',
      hoverBg: 'hover:bg-emerald-100'
    },
    { 
      key: 'good', 
      label: 'Good', 
      color: 'text-info-blue', 
      bg: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100'
    },
    { 
      key: 'warning', 
      label: 'Warning', 
      color: 'text-warning-amber', 
      bg: 'bg-amber-50',
      hoverBg: 'hover:bg-amber-100'
    },
    { 
      key: 'poor', 
      label: 'Poor', 
      color: 'text-orange-600', 
      bg: 'bg-orange-50',
      hoverBg: 'hover:bg-orange-100'
    },
    { 
      key: 'critical', 
      label: 'Critical', 
      color: 'text-critical-red', 
      bg: 'bg-red-50',
      hoverBg: 'hover:bg-red-100'
    }
  ]

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Equipment Health Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {healthCategories.map((category) => (
              <div key={category.key} className={`text-center p-4 ${category.bg} rounded-lg animate-pulse`}>
                <div className="w-8 h-8 bg-gray-200 rounded mx-auto mb-1"></div>
                <div className="w-16 h-4 bg-gray-200 rounded mx-auto"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Equipment Health Overview</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {healthCategories.map((category) => {
            const count = healthData?.[category.key as keyof typeof healthData] || 0
            
            return (
              <div 
                key={category.key}
                className={`text-center p-4 ${category.bg} ${category.hoverBg} rounded-lg cursor-pointer transition-colors`}
                onClick={() => onViewEquipment(category.key)}
              >
                <div className={`text-2xl font-bold ${category.color} mb-1`}>
                  {count}
                </div>
                <div className="text-sm text-gray-600">{category.label}</div>
              </div>
            )
          })}
        </div>
        
        {/* Health Score Summary */}
        {healthData && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.values(healthData).reduce((sum, count) => sum + count, 0)}
                </p>
                <p className="text-sm text-gray-600">Total Equipment</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-success-green">
                  {Math.round(((healthData.excellent + healthData.good) / Object.values(healthData).reduce((sum, count) => sum + count, 1)) * 100)}%
                </p>
                <p className="text-sm text-gray-600">Healthy Equipment</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-critical-red">
                  {healthData.critical + healthData.poor}
                </p>
                <p className="text-sm text-gray-600">Needs Attention</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}